import React, { useState } from 'react';
import { DatePicker } from "../../shared/ui";
import { interactionsApi } from '../../api/interactionsApi';
import { getApiError } from '../../api/client';

const InteractionUploadDialog = ({ projectId, onClose, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('upload'); // 'upload' or 'paste'
  const [title, setTitle] = useState('');
  const [interactionType, setInteractionType] = useState('meeting_notes');
  const [sourceDate, setSourceDate] = useState('');
  const [participants, setParticipants] = useState('');
  
  // Upload specific state
  const [files, setFiles] = useState(null);
  
  // Paste specific state
  const [rawText, setRawText] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (activeTab === 'upload') {
        const formData = new FormData();
        formData.append('title', title);
        formData.append('interaction_type', interactionType);
        if (sourceDate) formData.append('source_date', sourceDate);
        if (participants) formData.append('participants', participants);
        
        if (files) {
          for (let i = 0; i < files.length; i++) {
            formData.append('files', files[i]);
          }
        }
        await interactionsApi.uploadFiles(projectId, formData);
      } else {
        await interactionsApi.pasteText(projectId, {
          title,
          interaction_type: interactionType,
          raw_text: rawText,
          source_date: sourceDate || null,
          participants: participants ? participants.split(',').map(p => p.trim()) : null
        });
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">Add Interaction Context</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            &times;
          </button>
        </div>

        <div className="flex space-x-4 mb-4 border-b">
          <button
            className={`pb-2 ${activeTab === 'upload' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
            onClick={() => setActiveTab('upload')}
          >
            Upload Files
          </button>
          <button
            className={`pb-2 ${activeTab === 'paste' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
            onClick={() => setActiveTab('paste')}
          >
            Paste Text
          </button>
        </div>

        {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                required
                className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Kickoff Meeting"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
                value={interactionType}
                onChange={(e) => setInteractionType(e.target.value)}
              >
                <option value="meeting_notes">Meeting Notes</option>
                <option value="email_thread">Email Thread</option>
                <option value="slack_teams_export">Slack/Teams Export</option>
                <option value="workshop_summary">Workshop Summary</option>
                <option value="client_feedback">Client Feedback</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date (Optional)</label>
              <DatePicker
                bare
                className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
                value={sourceDate}
                onChange={setSourceDate}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Participants (Comma separated)</label>
              <input
                type="text"
                className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
                value={participants}
                onChange={(e) => setParticipants(e.target.value)}
                placeholder="John, Jane, Client"
              />
            </div>
          </div>

          <div className="mb-6">
            {activeTab === 'upload' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Files (PDF, DOCX, TXT, EML, MSG)</label>
                <input
                  type="file"
                  multiple
                  required
                  accept=".pdf,.docx,.txt,.eml,.msg"
                  className="w-full p-2 border rounded"
                  onChange={(e) => setFiles(e.target.files)}
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Raw Text</label>
                <textarea
                  required
                  rows="6"
                  className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500"
                  value={rawText}
                  onChange={(e) => setRawText(e.target.value)}
                  placeholder="Paste your meeting notes or email transcript here..."
                />
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded text-gray-600 hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Context'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InteractionUploadDialog;
